/**
 * BottomNav
 * CCH Care — Phase 2 → Phase 5
 * Last Modified: 2026-05-14
 *
 * Fixed bottom navigation with tabs + More overflow.
 *
 * Changes (2026-05-14) — Per-tab role gating + CNA self-report:
 * - Refactored two-array (BASE_TABS / SUPERVISOR_TABS) layout into a
 *   single ALL_TABS list with per-tab visible(role) predicates. Each
 *   tab's gate now sits next to its definition rather than being
 *   implicit in which array contains it. Easier to add tabs without
 *   re-thinking the role split, and easier to audit.
 * - Residents tab now hidden from CNAs (and any role lacking
 *   patients.view_all). Backend already 403s residents/directory.php
 *   for these roles (5/10 endpoint hardening); this stops the click
 *   into a guaranteed-403 page in the first place.
 * - Reports tab gate widened to include Physician and Nurse roles.
 *   Backend CARE_ROLE_PERMISSIONS grants both reports.view and
 *   patients.view_all to those roles, but the previous frontend split
 *   put them on BASE_TABS so they were missing Reports entirely.
 *   Pre-existing bug, surfaced while doing the CNA gate.
 * - Reports tab also visible to CNAs now — renders MyActivityView on
 *   ReportsPage when role lacks reports.view (CNAs see only their own
 *   documentation via the new reports.view_own permission + the
 *   my_activity backend action).
 * - VIEW_ALL_ROLES constant defines the set with both Reports and
 *   Residents access. Mirrors patients.view_all in
 *   includes/functions.php's CARE_ROLE_PERMISSIONS. Keep in sync if
 *   the backend matrix changes.
 *
 * Phase 5: Reports tab added for Care Supervisor / Care Admin roles.
 *
 * Changes (2026-04-22):
 * - Removed the context-aware FAB. Every tab already exposes its
 *   primary action in-place (Start ADL on each resident card, + Add
 *   Note at the top of the Current Shift section, etc.), so a global
 *   FAB duplicated existing affordances without adding value. The
 *   onFabPress handler in AppShell was also never implemented beyond
 *   a console.log stub. If a future feature genuinely needs a FAB,
 *   it should be built purpose-specific for that feature rather than
 *   retrofitting a generic one. FAB_CONFIG and onFabPress prop are
 *   both gone.
 */
import { useAuth } from '../../auth/useAuth';
import { COLORS } from '../../../config/constants';

// Roles with patients.view_all + reports.view backend permissions.
// Mirrors CARE_ROLE_PERMISSIONS in includes/functions.php. Add a role
// here ONLY if the backend matrix also grants it those permissions —
// otherwise the tab is visible but every click 403s.
const VIEW_ALL_ROLES = ['Care Supervisor', 'Care Admin', 'Physician', 'Nurse'];
const canViewAll = (role) => VIEW_ALL_ROLES.includes(role);

// Reports visibility: VIEW_ALL_ROLES see facility-wide reports;
// CNAs see their own activity (my_activity action, gated server-side
// by reports.view_own). ReportsPage branches on role to render the
// right view.
const canViewReports = (role) => canViewAll(role) || role === 'CNA';

const ALL_TABS = [
  { key: 'dashboard', icon: '🏠',  label: 'Dashboard', visible: () => true },
  { key: 'residents', icon: '👥',  label: 'Residents', visible: canViewAll },
  { key: 'reports',   icon: '📊',  label: 'Reports',   visible: canViewReports },
  { key: 'messages',  icon: '💬',  label: 'Messages',  visible: () => true },
  { key: 'more',      icon: '•••', label: 'More',      visible: () => true },
];

export default function BottomNav({ activeTab, onTabChange }) {
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';
  const TABS = ALL_TABS.filter(t => t.visible(role));

  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
      display: 'flex', justifyContent: 'space-around',
      padding: '6px 0',
      paddingBottom: 'max(6px, env(safe-area-inset-bottom))',
      zIndex: 40,
    }}>
      {TABS.map(tab => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 2, padding: '6px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: isActive ? COLORS.primary : COLORS.textMuted,
            }}
          >
            <span style={{ fontSize: 20 }}>{tab.icon}</span>
            <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500 }}>
              {tab.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}